import { useState, useEffect, useMemo } from 'react';
import { Box, Button, Paper, CircularProgress } from '@material-ui/core';
import MultiSelectDropdown from '../MultiSelectDropdown';
import { IClinicOptionsList } from 'model/clinic';
import { GET_CLINICS_OPTIONS } from 'api/graphql/v2/queries/Clinics';
import { useLazyQuery, useQuery } from 'react-apollo';
import { GET_ATTENDEES } from 'api/graphql/v2/queries/Attendees';
import { alphabeticalSort } from 'utils/sort';
import './BTTab.css';
import AutocompleteSearch from '../AutocompleteSearch';
import DataGrid from 'views/containers/datagrid';
import { useBTTabColumns } from './BTTabColumns';
import {
  GET_PROVIDER_SCHEDULE_SUMMARY,
  GET_SHIFT_DROPDOWN_OPTIONS
} from 'api/graphql/v2/queries/Providers';
import {
  IBTTabRow,
  IProviderScheduleSummary,
  IOptionType,
  ISchedulingShiftDropdownOptions,
  IShift
} from 'model/v2';
import { btLabelOf } from '../btLabel';

const statusFilterIds = [1, 2, 3, 4];

export default function BTTab() {
  const [selectedClinics, setSelectedClinics] = useState<string[]>([]);
  const [shift, setShift] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [btId, setBtId] = useState<number>();
  const [status, setStatus] = useState<number[]>(statusFilterIds);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tableData, setTableData] = useState<IBTTabRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchSuggestions, setSearchSuggestions] = useState<IOptionType[]>([]);
  const [isSearchBTLoading, setIsSearchBTLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isBTSearchSelected, setIsBTSearchSelected] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const { columns } = useBTTabColumns();
  const [unscheduledSetShiftCount, setUnscheduledSetShiftCount] = useState(0);
  const [
    partiallyScheduledSetShiftCount,
    setPartiallyScheduledSetShiftCount
  ] = useState(0);
  const [
    unscheduledAdditionalHourCount,
    setUnscheduledAdditionalHourCount
  ] = useState(0);
  const [
    partiallyScheduledAdditionalHourCount,
    setPartiallyScheduledAdditionalHourCount
  ] = useState(0);
  const [hasAnyFilterSelected, setHasAnyFilterSelected] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearchClicked, setIsSearchClicked] = useState<boolean>(true);
  const [isFirstSearch, setIsFirstSearch] = useState<boolean>(true);

  const statusFilters = [
    { id: 1, label: 'Unscheduled Set Shift', count: unscheduledSetShiftCount },
    {
      id: 2,
      label: 'Partially Scheduled Set Shift',
      count: partiallyScheduledSetShiftCount
    },
    {
      id: 3,
      label: 'Unscheduled Additional Hours',
      count: unscheduledAdditionalHourCount
    },
    {
      id: 4,
      label: 'Partially Scheduled Additional Hours',
      count: partiallyScheduledAdditionalHourCount
    }
  ];

  const { data: clinicsData, loading: isLoadingClinics } = useQuery(
    GET_CLINICS_OPTIONS
  );
  const {
    data: shiftDropdownOptions,
    loading: isLoadingShiftDropdownOptions
  } = useQuery(GET_SHIFT_DROPDOWN_OPTIONS);
  const clinicsMap = useMemo(() => {
    const map = new Map();
    (clinicsData?.clinicOptionsList as IClinicOptionsList[])?.forEach(item => {
      map.set(item.id, {
        id: item.id,
        name: item.name,
        abbreviation: item.abbreviation,
        state: item.state
      });
    });
    return map;
  }, [clinicsData]);
  const clinicOptions = Array.from(clinicsMap?.values() || []).map(c => ({
    label:
      c.abbreviation && c.abbreviation !== '' && c.abbreviation !== null
        ? c.name + ' (' + c.abbreviation + ')'
        : c.name,
    value: c.id.toString()
  }));
  const shiftOptions = useMemo(() => {
    return (shiftDropdownOptions?.getShiftDropdownOptions as ISchedulingShiftDropdownOptions[])?.map(
      item => ({
        label: item.description,
        value: item.shiftId.toString()
      })
    );
  }, [shiftDropdownOptions]);
  const handleClinicApply = (selected: string[]) => {
    setSelectedClinics(selected);
  };

  const handleShiftApply = (selected: string[]) => {
    setShift(selected);
  };
  useEffect(() => {
    if (selectedClinics.length == 0 && btId == undefined && shift.length == 0) {
      setHasAnyFilterSelected(false);
      setHasSearched(false);
      setPage(0);
      setUnscheduledSetShiftCount(0);
      setPartiallyScheduledSetShiftCount(0);
      setUnscheduledAdditionalHourCount(0);
      setPartiallyScheduledAdditionalHourCount(0);
      setStatus(statusFilterIds);
      setTotalCount(0);
    } else {
      setHasAnyFilterSelected(true);
    }
  }, [selectedClinics, btId, shift]);
  const handleStatusChange = (id: number) => {
    let newStatus = status;

    if (newStatus.includes(id)) {
      newStatus = newStatus.filter(i => i !== id);
    } else {
      newStatus = [...newStatus, id];
    }
    setStatus(newStatus);
    console.log(newStatus, 'handleStatusChange in bt tab');

    setIsSearchLoading(true);
    fetchProviderScheduleSummaries({
      variables: {
        input: {
          providerId: btId,
          clinicIds: selectedClinics.map(c => Number(c)),
          setShiftIds: shift.map(s => Number(s)),
          isUnscheduledSetShift: !newStatus.includes(statusFilterIds[0]),
          isPartiallyScheduledSetShift: !newStatus.includes(statusFilterIds[1]),
          isUnscheduledAdditionalHours: !newStatus.includes(statusFilterIds[2]),
          isPartiallyScheduledAdditionalHours: !newStatus.includes(
            statusFilterIds[3]
          ),
          page: 0,
          pageSize: rowsPerPage,
          searchSetShiftsOnly: true,
          searchAdditionalShiftsOnly: true
        }
      }
    });
  };
  const [
    fetchProviderScheduleSummaries,
    { data: providerScheduleSummaries }
  ] = useLazyQuery(GET_PROVIDER_SCHEDULE_SUMMARY, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      const providerScheduleSummary =
        providerScheduleSummaries?.getProviderScheduleSummaries;
      const providerSchedules = providerScheduleSummary?.providerSchedules;
      const tableData = providerSchedules?.map(
        (providerSchedule: IProviderScheduleSummary) => ({
          bt: btLabelOf(providerSchedule.provider),
          clinic:
            providerSchedule.clinic.abbreviation && providerSchedule.clinic.name
              ? providerSchedule.clinic.name +
                ' (' +
                providerSchedule.clinic.abbreviation +
                ')'
              : providerSchedule.clinic.name
              ? providerSchedule.clinic.name
              : providerSchedule.clinic.abbreviation,
          setShift: providerSchedule.acceptedSetShift.map(
            (shift: IShift) => `${shift.dayOfWeek}\n${shift.times}`
          ),
          // Remaining column shows genuinely-open shift hours plus opening-soon
          // hours together. The API returns that combined view per tier under
          // remaining*ShiftHoursForUI (the plain remaining fields exclude
          // opening-soon so the automation doesn't offer still-covered slots).
          remainingSetShift: (providerSchedule.remainingSetShiftHoursForUI || []).map(
            (shift: IShift) => ({ text: `${shift.dayOfWeek}\n${shift.times}`, opensOn: shift.opensOn })
          ),
          additionalHours: providerSchedule.acceptedAdditionalShift.map(
            (shift: IShift) => `${shift.dayOfWeek}\n${shift.times}`
          ),
          remainingAdditional: (providerSchedule.remainingAdditionalShiftHoursForUI || []).map(
            (shift: IShift) => ({ text: `${shift.dayOfWeek}\n${shift.times}`, opensOn: shift.opensOn })
          ),
          btId: providerSchedule.provider.id,
          remainingSetShiftRaw: providerSchedule.remainingSetShiftHoursRaw,
          remainingAdditionalRaw: providerSchedule.remainingAdditionalShiftHoursRaw
        })
      )?.filter(
        // Don't render fully-booked BTs: a row with no remaining capacity in
        // either tier is hidden on the UI, even though the API still returns it
        // (e.g. for BI / includeFullyScheduled).
        (row: { remainingSetShift: unknown[]; remainingAdditional: unknown[] }) =>
          row.remainingSetShift.length > 0 || row.remainingAdditional.length > 0
      );
      setTableData(tableData);
      setTotalCount(providerScheduleSummary?.count || 0);
      setUnscheduledSetShiftCount(
        providerScheduleSummary?.unscheduledSetShiftCount || 0
      );
      setPartiallyScheduledSetShiftCount(
        providerScheduleSummary?.partiallyScheduledSetShiftCount || 0
      );
      setUnscheduledAdditionalHourCount(
        providerScheduleSummary?.unscheduledAdditionalHourCount || 0
      );
      setPartiallyScheduledAdditionalHourCount(
        providerScheduleSummary?.partiallyScheduledAdditionalHourCount || 0
      );
      setIsSearchLoading(false);
      setIsSearchClicked(false);
    },
    onError: error => {
      console.error('GraphQL query error:', error);
      setIsSearchLoading(false);
      setIsSearchClicked(false);
    }
  });

  const [fetchBTSearchSuggestions, { data: attendeesList }] = useLazyQuery(
    GET_ATTENDEES,
    {
      fetchPolicy: 'network-only',
      onCompleted: () => {
        const nonDisplayedAttendees = attendeesList?.attendees?.sort(
          (a1: IOptionType, a2: IOptionType) =>
            alphabeticalSort(a1.displayName, a2.displayName)
        );
        if (nonDisplayedAttendees && nonDisplayedAttendees.length > 0) {
          setSearchSuggestions(nonDisplayedAttendees);
        } else {
          setSearchSuggestions([
            { displayName: 'No BTs found', resultStatus: '__NO_RESULTS__' }
          ]);
        }
        setIsSearchBTLoading(false);
      }
    }
  );

  const handleClearBTSearch = () => {
    setSearch('');
    setBtId(undefined);
    setIsBTSearchSelected(false);
    setSearchSuggestions([]);
    setSearchOpen(false);
  };

  // Handler for Autocomplete input change
  const handleBTInputChange = (_: unknown, value: string, reason: string) => {
    if (reason === 'input') {
      setSearch(value);
      setIsBTSearchSelected(false);
      if (value.length >= 3) {
        setIsSearchBTLoading(true);
        setSearchOpen(true);
        fetchBTSearchSuggestions({
          variables: {
            name: value,
            isClient: false,
            isStaffPage: true
          }
        });
      } else {
        setSearchSuggestions([]);
        setSearchOpen(false);
        setIsSearchBTLoading(false);
        setBtId(undefined);
      }
    }
  };

  const handleBTChange = (_: unknown, value: IOptionType | string | null) => {
    if (
      value &&
      typeof value !== 'string' &&
      value.resultStatus !== '__NO_RESULTS__'
    ) {
      setSearch(value.displayName);
      setBtId(value.id);
      setIsBTSearchSelected(true);
    } else {
      setIsBTSearchSelected(false);
    }
  };

  useEffect(() => {
    const hasAnyFilter =
      selectedClinics.length > 0 || btId !== undefined || shift.length > 0;

    if (hasAnyFilter) {
      setIsSearchLoading(true);
      setPage(0);
      setTotalCount(0);
      setHasSearched(true);
      setUnscheduledSetShiftCount(0);
      setPartiallyScheduledSetShiftCount(0);
      setUnscheduledAdditionalHourCount(0);
      setPartiallyScheduledAdditionalHourCount(0);

      fetchProviderScheduleSummaries({
        variables: {
          input: {
            providerId: btId,
            clinicIds: selectedClinics.map(c => Number(c)),
            setShiftIds: shift.map(s => Number(s)),
            isUnscheduledSetShift: isFirstSearch
              ? true
              : statusFilterIds.every(id => status.includes(id))
              ? true
              : !status.includes(statusFilterIds[0]),
            isPartiallyScheduledSetShift: isFirstSearch
              ? true
              : statusFilterIds.every(id => status.includes(id))
              ? true
              : !status.includes(statusFilterIds[1]),
            isUnscheduledAdditionalHours: isFirstSearch
              ? true
              : statusFilterIds.every(id => status.includes(id))
              ? true
              : !status.includes(statusFilterIds[2]),
            isPartiallyScheduledAdditionalHours: isFirstSearch
              ? true
              : statusFilterIds.every(id => status.includes(id))
              ? true
              : !status.includes(statusFilterIds[3]),
            searchSetShiftsOnly: true,
            searchAdditionalShiftsOnly: true,
            page: 0,
            pageSize: rowsPerPage
          }
        }
      });
      setIsFirstSearch(false);
    } else {
      setTableData([]);
      setIsSearchLoading(false);
    }
  }, [selectedClinics, btId, shift, status, rowsPerPage]);

  const handlePageChange = (newPage: number) => {
    if (!hasSearched) return;
    setPage(newPage);
    // Call API for pagination
    if (selectedClinics.length > 0 || btId !== undefined || shift.length > 0) {
      setIsSearchLoading(true);
      fetchProviderScheduleSummaries({
        variables: {
          input: {
            providerId: btId,
            clinicIds: selectedClinics.map(c => Number(c)),
            setShiftIds: shift.map(s => Number(s)),
            isUnscheduledSetShift: !status.includes(statusFilterIds[0]),
            isPartiallyScheduledSetShift: !status.includes(statusFilterIds[1]),
            isUnscheduledAdditionalHours: !status.includes(statusFilterIds[2]),
            isPartiallyScheduledAdditionalHours: !status.includes(
              statusFilterIds[3]
            ),
            page: newPage,
            pageSize: rowsPerPage
          }
        }
      });
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(0);
    if (
      hasSearched &&
      (selectedClinics.length > 0 || btId !== undefined || shift.length > 0)
    ) {
      setIsSearchLoading(true);
      fetchProviderScheduleSummaries({
        variables: {
          input: {
            providerId: btId,
            clinicIds: selectedClinics.map(c => Number(c)),
            setShiftIds: shift.map(s => Number(s)),
            isUnscheduledSetShift: status.includes(statusFilterIds[0]),
            isPartiallyScheduledSetShift: status.includes(statusFilterIds[1]),
            isUnscheduledAdditionalHours: status.includes(statusFilterIds[2]),
            isPartiallyScheduledAdditionalHours: status.includes(
              statusFilterIds[3]
            ),
            searchSetShiftsOnly: true,
            searchAdditionalShiftsOnly: true,
            page: 0,
            pageSize: newPageSize
          }
        }
      });
    }
  };

  useEffect(() => {
    setPage(0);
    if (
      hasSearched &&
      (selectedClinics.length > 0 || btId !== undefined || shift.length > 0)
    ) {
      setIsSearchLoading(true);
      fetchProviderScheduleSummaries({
        variables: {
          input: {
            providerId: btId,
            clinicIds: selectedClinics.map(c => Number(c)),
            setShiftIds: shift.map(s => Number(s)),
            isUnscheduledSetShift: !status.includes(statusFilterIds[0]),
            isPartiallyScheduledSetShift: !status.includes(statusFilterIds[1]),
            isUnscheduledAdditionalHours: !status.includes(statusFilterIds[2]),
            isPartiallyScheduledAdditionalHours: !status.includes(
              statusFilterIds[3]
            ),
            page: 0,
            pageSize: rowsPerPage
          }
        }
      });
    }
  }, [rowsPerPage]);

  return (
    <Box style={{ backgroundColor: '#FCFCFC', padding: 20 }}>
      <Box display="flex" className="filterBar" alignItems="center">
        <Box flex={1} minWidth={0} mr={2}>
          <Box position="relative">
            <MultiSelectDropdown
              label="Clinic"
              options={clinicOptions}
              selected={selectedClinics}
              onApply={handleClinicApply}
              placeholder={isLoadingClinics ? 'Loading clinics...' : 'Clinic'}
              disabled={isLoadingClinics}
            />
            {isLoadingClinics && (
              <Box
                position="absolute"
                top="50%"
                right={40}
                style={{ transform: 'translateY(-50%)', zIndex: 1 }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Box>
        <Box flex={1} minWidth={0} mr={2}>
          <Box position="relative">
            <MultiSelectDropdown
              label="Remaining Shift"
              options={shiftOptions || []}
              selected={shift}
              onApply={handleShiftApply}
              placeholder="Remaining Shift"
              disabled={isLoadingShiftDropdownOptions}
            />
            {isLoadingShiftDropdownOptions && (
              <Box
                position="absolute"
                top="50%"
                right={40}
                style={{ transform: 'translateY(-50%)', zIndex: 1 }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Box>
        <Box flex={1} minWidth={0}>
          <AutocompleteSearch
            isClient={false}
            placeholder="Search BT"
            search={search}
            isSearchLoading={isSearchBTLoading}
            searchSuggestions={searchSuggestions}
            searchOpen={searchOpen}
            isSearchSelected={isBTSearchSelected}
            onInputChange={handleBTInputChange}
            onChange={handleBTChange}
            onClear={handleClearBTSearch}
            onOpen={() => {
              if (search.length >= 3) setSearchOpen(true);
            }}
            onClose={() => setSearchOpen(false)}
          />
        </Box>
      </Box>
      <Box display="flex" alignItems="center" mt={2}>
        <Box className="BTTabStatusBar">
          {statusFilters.map((f, idx) => {
            return (
              <Button
                key={f.label}
                className={
                  hasAnyFilterSelected
                    ? isSearchClicked
                      ? 'BTTabStatusButtonDisabled'
                      : statusFilterIds.every(v => status.includes(v))
                      ? 'BTTabStatusButtonInactive'
                      : !status.includes(f.id)
                      ? 'BTTabStatusButton'
                      : 'BTTabStatusButtonInactive'
                    : 'BTTabStatusButtonDisabled'
                }
                style={{
                  border: hasAnyFilterSelected
                    ? isSearchClicked
                      ? '1px solid #e0e0e0'
                      : '1px solid #6f42f5'
                    : '1px solid #e0e0e0',
                  borderRadius:
                    idx === 0
                      ? '4px 0 0 4px'
                      : idx === statusFilters.length - 1
                      ? '0 4px 4px 0'
                      : '0'
                }}
                onClick={() => handleStatusChange(f.id)}
                disabled={isSearchClicked}
              >
                {f.label} ({f.count})
              </Button>
            );
          })}
        </Box>
      </Box>
      <Paper style={{ marginBottom: 16, marginTop: 20 }}>
        <DataGrid
          columns={columns}
          data={tableData}
          total={totalCount}
          page={page + 1}
          pageSize={rowsPerPage}
          loading={isSearchLoading}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyText={
            hasAnyFilterSelected
              ? hasSearched
                ? 'No data available'
                : 'Select atleast one filter to show results.'
              : 'Select atleast one filter to show results.'
          }
        />
      </Paper>
    </Box>
  );
}
